// SettingsLayout — the calm shell for the settings surface, pure.
//
// WHERE settings live (.claude/research/design/02): settings is a DESTINATION you reach
// from the nav rail's quiet top workspace/account switch — not permanent
// chrome. The frame's rule is "one secondary at a time, calm". So settings is
// a full sectioned VIEW (a two-column [ section nav | active section ]), the
// boring, universally-understood pattern (GitHub/Linear/Stripe settings) that
// keeps the frame's own spatial model — nav on the left, content on the right.
// The layout is mount-agnostic: a shell can drop it into a full route OR a
// large Dialog/Sheet; this component only owns the internal two-column shape.
// (Rejected: horizontal Tabs — a vertical section list echoes the app frame's
// left-nav model and scales past a handful of sections; a Dialog-only shape —
// squeezing forms into an overlay fights the calm, and settings is a place you
// GO, not a per-material secondary.)
//
// The section nav is NAVIGATION between destinations, so the active item
// carries aria-current="page" (recognition over recall) — not a tablist. Below
// a narrow width it folds to a horizontal row above the content, so the frame
// never squeezes a form to unreadable.
//
// Register (.claude/research/design/06): near-monochrome; the active nav item reads by a
// sunken fill — no accent spent on navigation. No rule between the nav and
// the panel: whitespace separates them (pen G-02).
//
// Pure/props-driven & controlled: `activeKind` + `onSectionSelect` own the
// selection (state lives with the caller, changes report UP). `children` is
// the active panel the consumer renders for `activeKind` — the layout never
// knows a panel's shape. `navAriaLabel` names the <nav> landmark (caller-
// resolved Lingui string); item labels are caller-owned localised ReactNode.

import type { ReactNode } from "react";
import { Text } from "../../Text/Text.js";
import { ViewColumn, ViewHeader } from "../../ViewHeader/ViewHeader.js";
import type { SettingsNavItem, SettingsSectionKind } from "../types.js";
import "./SettingsLayout.css";

export type SettingsLayoutProps = {
    // The section nav entries, in display order.
    readonly sections: readonly SettingsNavItem[];
    // The controlled active section — the one whose panel is shown.
    readonly activeKind: SettingsSectionKind;
    readonly onSectionSelect: (kind: SettingsSectionKind) => void;
    // The view title (localised, caller-owned) — the shared ViewHeader (D41).
    readonly title: ReactNode;
    // Names the <nav> landmark for AT (caller-resolved Lingui string).
    readonly navAriaLabel: string;
    // The active section's panel, rendered by the consumer for `activeKind`.
    readonly children: ReactNode;
};

export const SettingsLayout = ({
    sections,
    activeKind,
    onSectionSelect,
    title,
    navAriaLabel,
    children,
}: SettingsLayoutProps) => (
    <ViewColumn>
        <ViewHeader title={title} />
        <div className="bilda-settings-layout__cols">
            <nav
                aria-label={navAriaLabel}
                className="bilda-settings-layout__nav"
            >
                {sections.map((section) => {
                    const active = section.kind === activeKind;
                    return (
                        <button
                            key={section.kind}
                            type="button"
                            className="bilda-settings-layout__nav-item"
                            aria-current={active ? "page" : undefined}
                            onClick={() => onSectionSelect(section.kind)}
                        >
                            {section.icon != null ? (
                                <span
                                    className="bilda-settings-layout__nav-icon"
                                    aria-hidden="true"
                                >
                                    {section.icon}
                                </span>
                            ) : null}
                            <Text
                                size="md"
                                className="bilda-settings-layout__nav-label"
                            >
                                {section.label}
                            </Text>
                        </button>
                    );
                })}
            </nav>
            <div className="bilda-settings-layout__content">{children}</div>
        </div>
    </ViewColumn>
);
